import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { PhoneIcon, UserIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { MessageBubble } from './MessageBubble';
import { ComposeMessage } from './ComposeMessage';
import { SmsConversationReallocationModal } from './SmsConversationReallocationModal';
import type { ConversationDetail, Message } from '../types/sms.types';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ConversationThreadProps {
  conversation: ConversationDetail | null;
  onSendMessage: (message: string) => Promise<void>;
  onConversationUpdated?: () => void;
  loading?: boolean;
}

interface MessageGroup {
  date: Date;
  messages: Message[];
}

export const ConversationThread: React.FC<ConversationThreadProps> = ({
  conversation,
  onSendMessage,
  onConversationUpdated,
  loading = false
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showReallocationModal, setShowReallocationModal] = useState(false);

  // Group messages by date
  const messageGroups = useMemo((): MessageGroup[] => {
    if (!conversation?.messages?.length) return [];

    const groups: MessageGroup[] = [];
    let currentGroup: MessageGroup | null = null;

    // Sort messages by timestamp (oldest first)
    const sortedMessages = [...conversation.messages].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    for (const message of sortedMessages) {
      const messageDate = new Date(message.timestamp);

      if (!currentGroup || !isSameDay(currentGroup.date, messageDate)) {
        currentGroup = { date: messageDate, messages: [] };
        groups.push(currentGroup);
      }

      currentGroup.messages.push(message);
    }

    return groups;
  }, [conversation?.messages]);

  const scrollToBottom = useCallback((smooth = true) => {
    requestAnimationFrame(() => {
      const container = messagesContainerRef.current;
      if (container) {
        if (smooth) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        } else {
          container.scrollTop = container.scrollHeight;
        }
      }
    });
  }, []);

  // Get the last message ID to detect when messages actually change
  const lastMessageId = conversation?.messages?.[conversation.messages.length - 1]?.id;

  // Scroll to bottom when loading finishes (data just arrived from server)
  useLayoutEffect(() => {
    if (!loading && messageGroups.length > 0) {
      // Immediate scroll in layout effect (before paint)
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [loading, messageGroups.length]);

  // Scroll to bottom when messages change (new message added or replaced)
  useEffect(() => {
    if (!loading && messageGroups.length > 0) {
      // Multiple delayed scrolls to handle any async rendering
      const timer1 = setTimeout(() => scrollToBottom(false), 50);
      const timer2 = setTimeout(() => scrollToBottom(false), 200);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [lastMessageId, loading, messageGroups.length, scrollToBottom]);

  // Also scroll when conversation changes (switching between conversations)
  useEffect(() => {
    if (conversation?.phoneNumber && !loading) {
      const timer = setTimeout(() => scrollToBottom(false), 100);
      return () => clearTimeout(timer);
    }
  }, [conversation?.phoneNumber, loading, scrollToBottom]);

  // Format date separator label
  const formatDateLabel = (date: Date): string => {
    if (isToday(date)) return "Aujourd'hui";
    if (isYesterday(date)) return 'Hier';
    return format(date, 'EEEE d MMMM yyyy', { locale: fr });
  };

  if (!conversation) {
    return (
      <div className="sms-thread-empty">
        <div className="sms-thread-empty-content">
          <div className="sms-thread-empty-icon-wrapper">
            <PhoneIcon className="sms-thread-empty-icon" />
          </div>
          <h3>Sélectionnez une conversation</h3>
          <p>Choisissez une conversation dans la liste<br />ou créez-en une nouvelle</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sms-thread">
      {/* Header */}
      <div className="sms-thread-header">
        <div className="sms-thread-avatar">
          {conversation.contact?.name ? (
            conversation.contact.name.charAt(0).toUpperCase()
          ) : (
            <UserIcon className="sms-thread-avatar-icon" />
          )}
        </div>
        <div className="sms-thread-info">
          <h3 className="sms-thread-name">
            {conversation.contact?.name || conversation.phoneNumber}
          </h3>
          <div className="sms-thread-meta">
            {conversation.contact?.name && (
              <span className="sms-thread-phone">{conversation.phoneNumber}</span>
            )}
            {conversation.contact?.affaireId && (
              <a
                href={`/admin/tracker/${conversation.contact.affaireId}`}
                className="sms-thread-affaire"
              >
                Dossier #{conversation.contact.affaireId}
              </a>
            )}
          </div>
        </div>
        <button
          className="sms-thread-reallocate-btn"
          onClick={() => setShowReallocationModal(true)}
          title="Attribuer à un dossier"
        >
          <ArrowsRightLeftIcon />
          <span>Attribuer</span>
        </button>
      </div>

      {/* Messages */}
      <div className="sms-thread-messages" ref={messagesContainerRef}>
        {loading ? (
          <div className="sms-thread-loading">
            <div className="sms-loading-spinner" />
          </div>
        ) : messageGroups.length === 0 ? (
          <div className="sms-thread-no-messages">
            <p>Aucun message</p>
            <p className="sms-thread-hint">Envoyez un message pour démarrer</p>
          </div>
        ) : (
          <div className="sms-messages-wrapper">
            {messageGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="sms-message-group">
                {/* Date separator */}
                <div className="sms-date-separator">
                  <span className="sms-date-label">{formatDateLabel(group.date)}</span>
                </div>

                {/* Messages for this date */}
                {group.messages.map((msg, msgIndex) => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    isFirstInGroup={msgIndex === 0 || group.messages[msgIndex - 1]?.direction !== msg.direction}
                    isLastInGroup={msgIndex === group.messages.length - 1 || group.messages[msgIndex + 1]?.direction !== msg.direction}
                  />
                ))}
              </div>
            ))}
            <div ref={messagesEndRef} className="sms-scroll-anchor" />
          </div>
        )}
      </div>

      {/* Compose */}
      <ComposeMessage
        onSend={onSendMessage}
        disabled={loading}
        placeholder="Écrire un message..."
      />

      {/* SMS Reallocation Modal */}
      {showReallocationModal && (
        <SmsConversationReallocationModal
          phoneNumber={conversation.phoneNumber}
          currentAffaireId={conversation.contact?.affaireId || null}
          onClose={() => setShowReallocationModal(false)}
          onSuccess={() => {
            setShowReallocationModal(false);
            onConversationUpdated?.();
          }}
        />
      )}
    </div>
  );
};

export default ConversationThread;
